import {
  ApplicationConfig,
  provideZoneChangeDetection,
  importProvidersFrom,
  APP_INITIALIZER,
  ErrorHandler,
  Injector,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { AppDialog } from './core/services/app-dialog/app-dialog';
import { AppErrorHandler } from './core/handlers/app-error-handler';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { routes } from './app.routes';
import {
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
} from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideClientHydration } from '@angular/platform-browser';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

import { ToastrModule } from 'ngx-toastr';
import { provideToastr } from 'ngx-toastr';

// icons
import { TablerIconsModule } from 'angular-tabler-icons';
/**
 * Only the icons the app can render. `import * as TablerIcons` registered all
 * 5 790, which shipped a 2.4 MB chunk AND made every `<i-tabler>` rebuild the
 * whole merged icon map on render (3.9 s of CPU on /kyc/kyc-lenders).
 * Generated — see scripts/generate-tabler-subset.js.
 */
import { usedTablerIcons } from './core/icons/tabler-icons-used';

// perfect scrollbar
import { NgScrollbarModule } from 'ngx-scrollbar';
import { NgxPermissionsModule } from 'ngx-permissions';
//Import all material modules
import { MaterialModule } from './material.module';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CalendarModule, DateAdapter as CalendarDateAdapter } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';

// code view
import { provideHighlightOptions } from 'ngx-highlightjs';
import 'highlight.js/styles/atom-one-dark.min.css';

import { MSAL_INSTANCE, MsalService, MsalGuard, MSAL_GUARD_CONFIG, MsalGuardConfiguration, MsalBroadcastService } from '@azure/msal-angular';
import { PublicClientApplication, Configuration, InteractionType, BrowserCacheLocation } from '@azure/msal-browser';
import { OAuthSettings } from 'src/oauth';

import { AuthService } from './core/services/auth.service';
import { JwtInterceptor } from './core/interceptors/jwt.interceptor';
import { LoadingService } from './core/services/loading.service';
import { OfficeoauthService } from './core/services/officeoauth.service';

import { LOCALE_ID } from '@angular/core';
import { registerLocaleData } from '@angular/common';

import localeFr from '@angular/common/locales/fr';
import { firstValueFrom } from 'rxjs';

// IMPORTS MATÉRIELS
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE, DateAdapter as MatDateAdapter } from '@angular/material/core';
import { CustomDateAdapter } from './core/directives/custom-date-adapter';

import { provideEnvironmentNgxMask, provideNgxMask } from 'ngx-mask';
import { HelperService } from './core/services/helper.service';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { VpnInterceptor } from './core/interceptors/vpn.interceptor';
import { MaintenanceInterceptor } from './core/interceptors/maintenance.interceptor';

export function HttpLoaderFactory(http: HttpClient): any {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

// Factory function modifiée pour gérer l'ordre d'initialisation
export function initializeApp(
  authService: AuthService,
  msalService: MsalService,
  loadingService: LoadingService,
  officeoauthService: OfficeoauthService,
  helperService: HelperService,
  router: Router
) {
  return async (): Promise<any> => {

    if (environment.maintenanceMode) {
      router.navigate(['/authentication/maintenance']);
      loadingService.hide();
      return Promise.resolve();
    }
    loadingService.show();

    await msalService.instance.initialize();

    try {
      await firstValueFrom(authService.checkSession());

      if (authService.currentUser()) {
        // HelperService reads the identity from AuthService on every access: only the
        // user catalog has to be warmed up here.
        await firstValueFrom(helperService.get_all_users());
      }
      setTimeout(() => loadingService.hide(), 500);
    } catch (error) {
      loadingService.hide();
    }

    return Promise.resolve();
  };
}

registerLocaleData(localeFr);

export const MY_DATE_FORMATS = {
  parse: {
    dateInput: 'dd/MM/yyyy',
  },
  display: {
    dateInput: 'dd/MM/yyyy',
    monthYearLabel: 'MMMM yyyy',
    dateA11yLabel: 'dd/MM/yyyy',
    monthYearA11yLabel: 'MMMM yyyy',
  },
};

export const appConfig: ApplicationConfig = {
  providers: [
    // The application dialog (AppDialog.fire) is static: it gets MatDialog from here.
    provideAppInitializer(() => AppDialog.attach(inject(Injector))),
    { provide: ErrorHandler, useClass: AppErrorHandler },
    { provide: LOCALE_ID, useValue: 'fr' },
    { provide: MatDateAdapter, useClass: CustomDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'fr-FR' },
    provideAnimationsAsync(), // required animations providers
    provideToastr(), // Toastr providers
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideHighlightOptions({
      coreLibraryLoader: () => import('highlight.js/lib/core'),
      lineNumbersLoader: () => import('ngx-highlightjs/line-numbers'), // Optional, add line numbers if needed
      languages: {
        typescript: () => import('highlight.js/lib/languages/typescript'),
        css: () => import('highlight.js/lib/languages/css'),
        xml: () => import('highlight.js/lib/languages/xml'),
      },
    }),
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      }),
      withComponentInputBinding()
    ),
    provideHttpClient(withInterceptorsFromDi()),
    provideClientHydration(),
    provideAnimationsAsync(),
    importProvidersFrom(
      FormsModule,
      ToastrModule.forRoot(),
      ReactiveFormsModule,
      MaterialModule,
      NgxPermissionsModule.forRoot(),
      TablerIconsModule.pick(usedTablerIcons),
      NgScrollbarModule,
      CalendarModule.forRoot({
        provide: CalendarDateAdapter,
        useFactory: adapterFactory,
      }),
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      })
    ),

    // MSAL
    {
      provide: MSAL_INSTANCE,
      useFactory: () => {
        const msalConfig: Configuration = {
          auth: {
            clientId: OAuthSettings.appId,
            authority: OAuthSettings.authority,
            redirectUri: OAuthSettings.redirectUri,
          },
          cache: {
            // LocalStorage keeps the Microsoft account and refresh token across
            // browser restarts (sessionStorage wiped them on every browser close,
            // forcing a full Microsoft re-login and losing the profile picture).
            cacheLocation: BrowserCacheLocation.LocalStorage,
            storeAuthStateInCookie: false,
          },
        };
        return new PublicClientApplication(msalConfig);
      },
    },
    {
      provide: MSAL_GUARD_CONFIG, useValue:
        {
          interactionType: InteractionType.Popup, // ou 'Popup' 
          authRequest:
          {
            scopes: ['user.read'],
          },
        } as MsalGuardConfiguration,
    },
    MsalService,
    MsalBroadcastService,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [AuthService, MsalService, LoadingService, OfficeoauthService, HelperService, Router],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: JwtInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: VpnInterceptor,
      multi: true,
    },
    // Registered last on purpose: Angular runs response handlers in
    // reverse-registration order, so this interceptor sees the 503
    // maintenance error first and redirects before any other reacts.
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MaintenanceInterceptor,
      multi: true,
    },
    provideEnvironmentNgxMask(),
  ],
};
